import { Box } from "@mui/material";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { DownArrow } from "../../../../assets/images/icons/DownArrow";
import { StructuredPromptCoordinate } from "../../../../models/structured-prompt";
import styles from "./FiboPromptTree.module.scss";

export type BBoxPoint = StructuredPromptCoordinate;
export const MIN_BBOX_SIZE = 5;

const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

interface NumberInputWithSteppersProps {
	value: number;
	onChange: (val: number) => void;
	disabled?: boolean;
	min: number;
	max: number;
}

export const NumberInputWithSteppers = ({ value, onChange, disabled, min, max }: NumberInputWithSteppersProps) => {
	const inputRef = useRef<HTMLInputElement>(null);

	const handleStep = (direction: "up" | "down") => {
		if (!inputRef.current || disabled) return;
		direction === "up" ? inputRef.current.stepUp() : inputRef.current.stepDown();
		onChange(clamp(parseInt(inputRef.current.value, 10) || 0, min, max));
	};

	return (
		<Box className={clsx(styles.numberInputWrapper, { [styles.disabled]: disabled })}>
			<input
				ref={inputRef}
				type="number"
				step="1"
				min={min}
				max={max}
				value={Math.round(value)}
				onChange={(e) => {
					const parsed = parseInt(e.target.value, 10);
					onChange(isNaN(parsed) ? min : clamp(parsed, min, max));
				}}
				className={styles.boundingBoxNumberInput}
				disabled={disabled}
			/>
			<Box className={styles.stepperButtons}>
				<button
					type="button"
					className={styles.stepButton}
					onClick={() => handleStep("up")}
					disabled={disabled}
				>
					<DownArrow className={styles.stepUpIcon} />
				</button>
				<button
					type="button"
					className={styles.stepButton}
					onClick={() => handleStep("down")}
					disabled={disabled}
				>
					<DownArrow className={styles.stepDownIcon} />
				</button>
			</Box>
		</Box>
	);
};

interface BoundingBoxInputProps {
	label: "top_left" | "bottom_right";
	value: BBoxPoint;
	onChange?: (label: "top_left" | "bottom_right", value: BBoxPoint) => void;
	disabled?: boolean;
}

export const BoundingBoxInput = ({ label, value, onChange, disabled }: BoundingBoxInputProps) => {
	const isTopLeft = label === "top_left";
	const min = isTopLeft ? 0 : MIN_BBOX_SIZE;
	const max = isTopLeft ? 100 - MIN_BBOX_SIZE : 100;

	const [localValue, setLocalValue] = useState<BBoxPoint>({ x: Math.round(value.x), y: Math.round(value.y) });
	const isInternalUpdate = useRef(false);

	useEffect(() => {
		if (isInternalUpdate.current) {
			isInternalUpdate.current = false;
			return;
		}
		const rounded = { x: Math.round(value.x), y: Math.round(value.y) };
		if (rounded.x !== localValue.x || rounded.y !== localValue.y) {
			setLocalValue(rounded);
		}
	}, [value.x, value.y]);

	const handleChange = (key: "x" | "y", newVal: number) => {
		const updated = { ...localValue, [key]: clamp(Math.round(newVal), min, max) };
		setLocalValue(updated);
		isInternalUpdate.current = true;
		onChange?.(label, updated);
	};

	return (
		<Box className={styles.boundingBoxRow}>
			<span className={styles.boundingBoxLabel}>{label}:</span>
			<Box className={styles.boundingBoxInputs}>
				<span>[</span>
				<NumberInputWithSteppers
					value={localValue.x}
					onChange={(v) => handleChange("x", v)}
					disabled={disabled}
					min={min}
					max={max}
				/>
				<span>,</span>
				<NumberInputWithSteppers
					value={localValue.y}
					onChange={(v) => handleChange("y", v)}
					disabled={disabled}
					min={min}
					max={max}
				/>
				<span>]</span>
			</Box>
		</Box>
	);
};
